'use client';

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { useGetAllStrategiesFilterOptionsQuery } from 'src/api/redux/strategy/allStrategiesApi';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDebounce } from 'use-debounce';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

interface City {
  id: string;
  name: string;
}

const StrategyCityDropdown: React.FC = (): React.ReactElement => {
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedCities,
    setSelectedCities,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [allCities, setAllCities] = useState<City[]>([]);
  const isLoadingNextPage = useRef<boolean>(false);

  const { data: cities, isFetching } = useGetAllStrategiesFilterOptionsQuery(
    {
      type: 'city',
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch,
      page,
      pageSize,
    },
    { skip: selectedChainIds.length === 0 }
  );

  const hasMore = cities?.results?.length === pageSize;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  useEffect(() => {
    setPage(1);
    setAllCities([]);
    isLoadingNextPage.current = false;
  }, [debouncedSearch, selectedChainIds, boundedTerritory?.id, selectedTerritory?.id]);

  useEffect(() => {
    if (cities?.results) {
      setAllCities((prev) => {
        const validCities = cities.results.filter((c: City) => c.name);
        if (page === 1) {
          isLoadingNextPage.current = false;
          return validCities;
        }
        const existingIds = new Set(prev.map((c: City) => c.id));
        const newCities = validCities.filter((c: City) => !existingIds.has(c.id));
        isLoadingNextPage.current = false;
        return [...prev, ...newCities];
      });
    }
  }, [cities, page]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasMore || isLoadingNextPage.current)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingNextPage.current = true;
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;

    if (!isOpen) {
      calculatePosition();
    }

    setIsOpen(!isOpen);
  };

  const handleCityToggle = (cityId: string) => {
    const updatedCities = selectedCities.includes(cityId)
      ? selectedCities.filter((c) => c !== cityId)
      : [...selectedCities, cityId];
    setSelectedCities(updatedCities);
  };

  const handleClearCities = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCities([]);
  };

  const getDisplayText = () => {
    if (selectedCities.length === 0) return 'Cities';
    if (selectedCities.length === 1) {
      return (
        allCities.find((c) => c.id === selectedCities[0])?.name ||
        selectedCities[0]
      );
    }
    return `${selectedCities.length} Cities`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faLocationDot}
        label="City"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={selectedCities.length > 0}
        onClear={handleClearCities}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-city-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search cities..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={selectedCities.length}
        onClearAll={() => setSelectedCities([])}
      >
        {allCities.map((city) => (
          <CheckboxListItem
            key={city.id}
            id={city.id}
            label={city.name}
            isSelected={selectedCities.includes(city.id)}
            onToggle={handleCityToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={allCities.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="cities"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategyCityDropdown;
